<!-- MobileBottomNav.vue -->
<!-- Descripción: Barra de navegación inferior flotante (pill Liquid Glass tipo iOS 26) para mobile. -->
<!-- Autor: Claude -->
<!-- Fecha: 2026-09-26 -->

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { Keyboard } from '@capacitor/keyboard'
import type { PluginListenerHandle } from '@capacitor/core'
import { isNativeApp } from '@/utils/platform'
import { useLiquidGlass } from '@/composables/useLiquidGlass'
import { useBottomNavItems } from '@/composables/useBottomNavItems'
import { PlusIcon } from '@heroicons/vue/24/outline'
import BottomNavItem from '@/components/MobileBottomNav/BottomNavItem.vue'

defineProps<{
  /** Auto-hide por scroll (transform en el pill, calculado en App.vue). */
  visible: boolean
}>()

const emit = defineEmits<{ 'open-new-appointment': [] }>()

// El FAB se inserta antes de este índice (entre Agenda y Citas).
const FAB_INDEX = 2

const { items, goTo } = useBottomNavItems()

const pill = ref<HTMLElement | null>(null)
const { filterId, refraction, lens, specularUrl } = useLiquidGlass(pill)

// @capacitor/keyboard ya era dependencia pero no estaba cableado; gateado a nativo para no afectar web.
let keyboardListeners: PluginListenerHandle[] = []
let unmounted = false
onMounted(async () => {
  if (!isNativeApp()) return
  const listeners = await Promise.all([
    Keyboard.addListener('keyboardWillShow', () => document.body.classList.add('keyboard-open')),
    Keyboard.addListener('keyboardWillHide', () => document.body.classList.remove('keyboard-open'))
  ])
  // Se desmontó mientras el await estaba en vuelo: onUnmounted ya corrió con el array vacío, así que hay que limpiar acá.
  if (unmounted) {
    listeners.forEach((listener) => listener.remove())
    return
  }
  keyboardListeners = listeners
})
onUnmounted(() => {
  unmounted = true
  keyboardListeners.forEach((listener) => listener.remove())
})
</script>

<template>
  <!-- Wrapper transparente a los clicks: solo el pill recibe eventos. -->
  <nav class="mobile-bottom-nav pointer-events-none fixed inset-x-0 bottom-0 z-40 px-4 pb-[var(--floating-nav-gap)] lg:hidden">
    <!-- Lente SVG que Chromium aplica al fondo vía backdrop-filter: url(#id). -->
    <svg v-if="refraction && lens" class="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <filter :id="filterId" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
        <feImage :href="lens.mapUrl" x="0" y="0" :width="lens.width" :height="lens.height" preserveAspectRatio="none" result="map" />
        <feDisplacementMap in="SourceGraphic" in2="map" :scale="lens.scale" xChannelSelector="R" yChannelSelector="G" result="bent" />
        <feGaussianBlur in="bent" stdDeviation="0.7" result="soft" />
        <feColorMatrix in="soft" type="saturate" values="1.3" />
      </filter>
    </svg>

    <!-- El transform del auto-hide va en el MISMO elemento que el backdrop-filter: en un ancestro rompe el muestreo. -->
    <div
      ref="pill"
      class="mbn-glass pointer-events-auto relative isolate mx-auto flex max-w-sm items-center justify-around gap-0.5 rounded-full px-2 py-1.5 transition-transform duration-[600ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
      :class="[visible ? 'translate-y-0' : 'translate-y-[calc(100%+var(--floating-nav-gap)+1rem)]', { 'mbn-glass--refract': refraction }]"
      :style="refraction ? { '--mbn-lens': `url(#${filterId})` } : undefined"
    >
      <span
        v-if="specularUrl"
        class="mbn-specular pointer-events-none absolute inset-0 -z-10 rounded-full"
        :style="{ backgroundImage: `url(${specularUrl})` }"
        aria-hidden="true"
      />

      <template v-for="(item, index) in items" :key="item.id">
        <!-- FAB: acción principal, adentro del pill (sin sobresalir). -->
        <button
          v-if="index === FAB_INDEX"
          type="button"
          aria-label="Nueva cita"
          title="Nueva cita"
          class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-light-accent text-white transition-opacity hover:opacity-90 dark:bg-dark-accent"
          @click="emit('open-new-appointment')"
        >
          <PlusIcon class="h-6 w-6" />
        </button>

        <BottomNavItem
          :label="item.label"
          :icon="item.icon"
          :active="item.active"
          @click="goTo(item.path)"
        />
      </template>

    </div>
  </nav>
</template>

<style scoped>
/* Vidrio claro (WebKit/Gecko y Chromium mientras se genera el mapa): casi sin tinte ni blur. */
.mbn-glass {
  background: rgb(255 255 255 / 0.32);
  -webkit-backdrop-filter: blur(5px) saturate(160%);
  backdrop-filter: blur(5px) saturate(160%);
  border: 1px solid rgb(255 255 255 / 0.5);
  box-shadow:
    inset 0 1px 1px 0 rgb(255 255 255 / 0.85),
    inset 0 0 6px 0 rgb(255 255 255 / 0.25),
    inset 0 -2px 6px -2px rgb(0 0 0 / 0.08),
    0 0 0 0.5px rgb(0 0 0 / 0.08),
    0 12px 36px -10px rgb(0 0 0 / 0.28);
}

/* Oscuro: vidrio ahumado, como una botella teñida; la lupa y el canto siguen igual. */
.dark .mbn-glass {
  background: linear-gradient(180deg, rgb(18 16 19 / 0.7) 0%, rgb(6 5 7 / 0.82) 100%);
  border-color: rgb(255 255 255 / 0.07);
  box-shadow:
    inset 0 1px 1px 0 rgb(255 255 255 / 0.08),
    inset 0 0 6px 0 rgb(255 255 255 / 0.02),
    0 0 0 0.5px rgb(0 0 0 / 0.4),
    0 12px 36px -10px rgb(0 0 0 / 0.6);
}

/* Chromium con mapa listo: la refracción del canto hace el trabajo; el centro queda nítido. */
.mbn-glass--refract {
  background: rgb(255 255 255 / 0.32);
  backdrop-filter: var(--mbn-lens) blur(1px) brightness(1.04);
}

.dark .mbn-glass--refract {
  background: linear-gradient(180deg, rgb(18 16 19 / 0.64) 0%, rgb(6 5 7 / 0.78) 100%);
  backdrop-filter: var(--mbn-lens) blur(1px) brightness(0.85) saturate(1.2);
}

.mbn-specular { background-size: 100% 100%; }
/* En ahumado el brillo del canto se lee como un borde blanco: apenas un reflejo. */
.dark .mbn-specular { opacity: 0.12; }

/* Sin backdrop-filter o con "reducir transparencia", fondo sólido para no perderse contra el contenido. */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .mbn-glass { background: rgb(249 248 247 / 0.98); }
  .dark .mbn-glass { background: rgb(31 29 32 / 0.98); }
}

@media (prefers-reduced-transparency: reduce) {
  .mbn-glass,
  .mbn-glass--refract {
    background: rgb(249 248 247 / 0.98);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  .dark .mbn-glass,
  .dark .mbn-glass--refract {
    background: rgb(31 29 32 / 0.98);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  .mbn-specular { display: none; }
}
</style>
